
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';
import DownloadExcel from 'vue-json-excel/JsonExcel.vue';

// --- Models ---
import { Vehicle } from '@/models/Vehicle.model';

// --- Services ---
import AppService from '@/services/app';
import JobCardService from '@/services/jobCard';
import VehicleService from '@/services/vehicle';

// --- Third Party ---
import dayjs from 'dayjs';

@Component({
  components: {
    DownloadExcel,
  },
})
export default class VehicleListing extends Vue {
  // used by Filter Area
  search: string = '';
  filterOptions: string[] = ['Active', 'Service Due', 'Wheel Alignment Due', 'COF Due', 'License Due', 'Disposed'];
  vehicleFilter: string = 'Active';
  sortBy: string = 'code';
  sortDescending: boolean = true;

  // used by No Data Slot
  isLoading: boolean = true;
  displayVehicles: Vehicle[] = [];

  isView: boolean = false;

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  title: string = '';
  clickedItem: Vehicle = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);

  valid: boolean = false;

  licenseEndDatePicker: boolean = false;
  roadworthDatePicker: boolean = false;

  createDisplayLicenseJobCard: boolean = false;
  unlinkedTrailers: Vehicle[] = [];
  frontTrailer?: Vehicle;

  rules = {
    required: (value: string) => !!value || '',
    requiredNumber: (value: number) => value != null && value !== 0 || '',
    requiredObject: (value: any) => value.id > 0,
    optionalCellNumber: (value: string) => (value != null && value.length > 0) ?  (/^[0-9]{10}$/.test(value) || 'Please enter a valid Phone Number') : true,
    numbersOnly: (value: string) => (/^\d+$/.test(value)) || 'Invalid character',
    optionalNumbersOnly: (value: string) => (value != null && value.length > 0) ?  (/^\d+$/.test(value)) || 'Invalid character' : true,
    maxOdometer: (value: number) => value < 2000000 || 'Invalid',
  };

  clicked: boolean = false;

  // used by Vehicle Disposal Dialog
  showVehicleDisposalDialog: boolean = false;
  disposalDatePicker: boolean = false;
  validDisposal: boolean = false;
  clickedDisposal: boolean = false;

  // used by Confirm Action Dialog for Activate/Suspend Item
  action: string = '';
  dialogContent: string = '';
  showConfirmActionDialog: boolean = false;
  confirmActionDelay: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      const headers = [];

      if (this.vehicleFilter !== 'Disposed') {
        headers.push({
          sortable: false,
          text: '',
          value: 'serviceWarning',
          align: '',
        });
      }

      headers.push({
        sortable: true,
        text: 'Type',
        value: 'vehicleTypeId',
        align: '',
      });

      headers.push({
        sortable: true,
        text: 'Code',
        value: 'code',
        align: '',
      });

      headers.push({
        sortable: true,
        text: 'Model',
        value: 'model',
        align: '',
      });

      headers.push({
        sortable: true,
        text: 'Registration',
        value: 'registrationNumber',
        align: '',
      });

      if (this.vehicleFilter === 'Disposed') {
        headers.push({
          sortable: true,
          text: 'Disposal Date',
          value: 'disposalDate',
          align: '',
        });

        headers.push({
          sortable: true,
          text: 'Reason',
          value: 'disposalReason',
          align: '',
        });

      } else {
        /*
        headers.push({
          sortable: true,
          text: 'VIN',
          value: 'vin',
          align: '',
        });
        */

        headers.push({
          sortable: true,
          text: 'Odo',
          value: 'odometer',
          align: '',
        });

        if (this.vehicleFilter === 'Wheel Alignment Due') {
          headers.push({
            sortable: true,
            text: 'Wheel Alignment Due At',
            value: 'nextWheelAlignmentOdo',
            align: '',
          });

          headers.push({
            sortable: true,
            text: 'Difference',
            value: 'wheelDifference',
            align: '',
          });
        } else {
          headers.push({
            sortable: true,
            text: 'Service Due At',
            value: 'nextServiceOdo',
            align: '',
          });
        }

        if (this.vehicleFilter === 'Service Due') {
          headers.push({
            sortable: true,
            text: 'Difference',
            value: 'serviceDifference',
            align: '',
          });
        }

        /*
        if (this.vehicleFilter !== 'Wheel Alignment Due' && this.vehicleFilter !== 'Service Due') {
          headers.push({
            sortable: true,
            text: 'Cell Number',
            value: 'cellNumber',
            align: '',
          });
        }
        */

        headers.push({
          sortable: true,
          text: 'License Expiry',
          value: 'licenseEndDate',
          align: '',
        });
      }

      headers.push({
        sortable: true,
        text: 'Status',
        value: 'vehicleStatusId',
        align: '',
      });

      headers.push({
        sortable: false,
        text: '',
        value: 'action',
        align: 'end',
      });

      return headers;
    }

    customFilter(column: string, search: string, item: Vehicle) {
      let result: boolean = false;
      
      if (search === '') {
        result = true;
      } else {
        result = item.vehicleType.toLowerCase().includes(search.toLowerCase()) ||
          item.vehicleStatus.toLowerCase().includes(search.toLowerCase()) ||
          item.code.toLowerCase().includes(search.toLowerCase()) ||
          item.yearModel.toLowerCase().includes(search.toLowerCase()) ||
          item.make.toLowerCase().includes(search.toLowerCase()) ||
          item.model.toLowerCase().includes(search.toLowerCase()) ||
          item.registrationNumber.toLowerCase().includes(search.toLowerCase()) ||
          item.vin.toLowerCase().includes(search.toLowerCase()) ||
          ('' + item.odometer).includes(search) ||
          ('' + item.nextServiceOdo).includes(search) ||
          (item.cellNumber ? item.cellNumber.toLowerCase().includes(search.toLowerCase()) : false) ||
          item.licenseEndDate.toLowerCase().includes(search.toLowerCase());
      }

      return result;      
    }

    customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
      // If the result is negative, a is sorted before b.
      // If the result is positive, b is sorted before a.
      // If the result is 0, no changes are done with the sort order of the two values.

      switch (sortBy[0]) {
        case 'serviceWarning':
          switch (this.vehicleFilter) {
            case ('Service Due'):
              items.sort((a: Vehicle, b: Vehicle) => {
                if (this.showVehicleWarning('service', a) === 'overdue') {
                  return -1;
                } else if (this.showVehicleWarning('service', b) === 'overdue') {
                  return 1;
                } else if (this.showVehicleWarning('service', a) === 'service-due') {
                  return -1;
                } else if (this.showVehicleWarning('service', b) === 'service-due') {
                  return 1;
                } else {
                  return 0;
                }
              });
              break;

            case ('Wheel Alignment Due'):
              items.sort((a: Vehicle, b: Vehicle) => {
                if (this.showVehicleWarning('wheel', a) === 'overdue') {
                  return -1;
                } else if (this.showVehicleWarning('wheel', b) === 'overdue') {
                  return 1;
                } else if (this.showVehicleWarning('wheel', a) === 'service-due') {
                  return -1;
                } else if (this.showVehicleWarning('wheel', b) === 'service-due') {
                  return 1;
                } else {
                  return 0;
                }
              });
              break;
          }
          break;

        case 'vehicleTypeId':
          // vehicleTypeId is a number
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.vehicleTypeId > b.vehicleTypeId) {
                sort = -1;
              } else if (b.vehicleTypeId > a.vehicleTypeId) {
                sort = 1;
              }
              return sort;
            });
          } else {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.vehicleTypeId > b.vehicleTypeId) {
                sort = 1;
              } else if (b.vehicleTypeId > a.vehicleTypeId) {
                sort = -1;
              }
              return sort;
            });
          }
          break;

        case 'code':
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => a.code.toLowerCase().localeCompare(b.code.toLowerCase()));
          } else {
            items.sort((a: Vehicle, b: Vehicle) => b.code.toLowerCase().localeCompare(a.code.toLowerCase()));
          }
          break;

        case 'model':
          // {{ item.yearModel + ' ' +  item.make + ' ' + item.model }}
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => 
              (a.yearModel + ' ' + a.make + ' ' + a.model).localeCompare(b.yearModel + ' ' + b.make + ' ' + b.model));
          } else {
            items.sort((a: Vehicle, b: Vehicle) => 
              (b.yearModel + ' ' + b.make + ' ' + b.model).localeCompare(a.yearModel + ' ' + a.make + ' ' + a.model));
          }
          break;

        case 'registrationNumber':
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => a.registrationNumber.toLowerCase().localeCompare(b.registrationNumber.toLowerCase()));
          } else {
            items.sort((a: Vehicle, b: Vehicle) => b.registrationNumber.toLowerCase().localeCompare(a.registrationNumber.toLowerCase()));
          }
          break;

        case 'odometer':
          // odometer is a number
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.odometer > b.odometer) {
                sort = -1;
              } else if (b.odometer > a.odometer) {
                sort = 1;
              }
              return sort;
            });
          } else {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.odometer > b.odometer) {
                sort = 1;
              } else if (b.odometer > a.odometer) {
                sort = -1;
              }
              return sort;
            });
          }
          break;

        case 'nextServiceOdo':
          // nextServiceOdo is a number
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.nextServiceOdo > b.nextServiceOdo) {
                sort = -1;
              } else if (b.nextServiceOdo > a.nextServiceOdo) {
                sort = 1;
              }
              return sort;
            });
          } else {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.nextServiceOdo > b.nextServiceOdo) {
                sort = 1;
              } else if (b.nextServiceOdo > a.nextServiceOdo) {
                sort = -1;
              }
              return sort;
            });
          }
          break;

        case 'serviceDifference':
          // serviceDifference is a number
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.nextServiceOdo - a.odometer > b.nextServiceOdo - b.odometer) {
                sort = -1;
              } else if (b.nextServiceOdo - b.odometer > a.nextServiceOdo - a.odometer) {
                sort = 1;
              }
              return sort;
            });
          } else {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.nextServiceOdo - a.odometer > b.nextServiceOdo - b.odometer) {
                sort = 1;
              } else if (b.nextServiceOdo - b.odometer > a.nextServiceOdo - a.odometer) {
                sort = -1;
              }
              return sort;
            });
          }
          break;

        case 'nextWheelAlignmentOdo':
          // nextWheelAlignmentOdo is a number
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.nextWheelAlignmentOdo > b.nextWheelAlignmentOdo) {
                sort = -1;
              } else if (b.nextWheelAlignmentOdo > a.nextWheelAlignmentOdo) {
                sort = 1;
              }
              return sort;
            });
          } else {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.nextWheelAlignmentOdo > b.nextWheelAlignmentOdo) {
                sort = 1;
              } else if (b.nextWheelAlignmentOdo > a.nextWheelAlignmentOdo) {
                sort = -1;
              }
              return sort;
            });
          }
          break;

        case 'wheelDifference':
          // wheelDifference is a number
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.nextWheelAlignmentOdo - a.odometer > b.nextWheelAlignmentOdo - b.odometer) {
                sort = -1;
              } else if (b.nextWheelAlignmentOdo - b.odometer > a.nextWheelAlignmentOdo - a.odometer) {
                sort = 1;
              }
              return sort;
            });
          } else {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.nextWheelAlignmentOdo - a.odometer > b.nextWheelAlignmentOdo - b.odometer) {
                sort = 1;
              } else if (b.nextWheelAlignmentOdo - b.odometer > a.nextWheelAlignmentOdo - a.odometer) {
                sort = -1;
              }
              return sort;
            });
          }
          break;

        case 'licenseEndDate':
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => a.licenseEndDate.toLowerCase().localeCompare(b.licenseEndDate.toLowerCase()));
          } else {
            items.sort((a: Vehicle, b: Vehicle) => b.licenseEndDate.toLowerCase().localeCompare(a.licenseEndDate.toLowerCase()));
          }
          break;

        case 'disposalDate':
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => (a.disposalDate ? a.disposalDate : '').toLowerCase().localeCompare((b.disposalDate ? b.disposalDate : '').toLowerCase()));
          } else {
            items.sort((a: Vehicle, b: Vehicle) => (b.disposalDate ? b.disposalDate : '').toLowerCase().localeCompare((a.disposalDate ? a.disposalDate : '').toLowerCase()));
          }
          break;

        case 'vehicleStatusId':
          // vehicleStatusId is a number
          if (sortDesc[0]) {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.vehicleStatusId > b.vehicleStatusId) {
                sort = -1;
              } else if (b.vehicleStatusId > a.vehicleStatusId) {
                sort = 1;
              }
              return sort;
            });
          } else {
            items.sort((a: Vehicle, b: Vehicle) => {
              let sort = 0;

              if (a.vehicleStatusId > b.vehicleStatusId) {
                sort = 1;
              } else if (b.vehicleStatusId > a.vehicleStatusId) {
                sort = -1;
              }
              return sort;
            });
          }
          break;
      }

      return items;
    }

    async beforeMount() {
      await AppService.listStatuses(false);
      await VehicleService.listTypes(false);
      await JobCardService.listOpen(true); 
      await this.listActive(true); 
      
      this.filterVehicles();
    }

    getToday() {
      return dayjs().format('YYYY-MM-DD');
    }

    // still required by the input fields on Vehicle
    showVehicleWarning(type: string, item: Vehicle) {
      if (!item.disposalDate) {
        return this.$store.getters['vehicles/showWarning'](type, item);
      }
      return '';
    }

    async listActive(refreshFromAPI: boolean) {
      try {
        this.isLoading = true;
        const response: string = await VehicleService.listActive(refreshFromAPI);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    async listDisposed(refreshFromAPI: boolean) {
      try {
        this.isLoading = true;
        const response: string = await VehicleService.listDisposed(refreshFromAPI);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    async filterVehicles() {
      switch (this.vehicleFilter) {
        case ('Active'):
          await this.listActive(false);
          this.displayVehicles = this.$store.getters['vehicles/activeVehicles'];
          this.sortBy = 'code';
          this.sortDescending = true;
          break;

        case ('Service Due'):
          this.displayVehicles = this.$store.getters['vehicles/activeVehicles'].filter((item: Vehicle) => item.odometer + 5000 > item.nextServiceOdo);
          this.sortBy = 'serviceDifference';
          this.sortDescending = false;
          break;

        case ('Wheel Alignment Due'):
          this.displayVehicles = this.$store.getters['vehicles/activeVehicles'].filter((item: Vehicle) => item.odometer + 5000 > item.nextWheelAlignmentOdo);
          this.sortBy = 'wheelDifference';
          this.sortDescending = false;
          break;

        case ('COF Due'):
          // #152 Updated to show COF Due in next 2 months so they can plan for Prep
          this.displayVehicles = this.$store.getters['vehicles/activeVehicles'].filter((item: Vehicle) => {
            const licenseEndDate = dayjs(item.licenseEndDate);
            const roadworthyDate = dayjs(item.roadworthyDate);
            // check if the license is due? (today is less than 60d until license date) 
            if (dayjs().isAfter(licenseEndDate.subtract(60, 'days'))) {
              // if license date is in the past
              if (dayjs().isAfter(licenseEndDate)) {
                // then check if the roadworthy date is less than 60d until today
                if (roadworthyDate.isBefore(dayjs().subtract(60, 'days'))) {
                  return true;
                }
              } else {
                // check if the roadworthy date is less than 60d until license date
                if (roadworthyDate.isBefore(licenseEndDate.subtract(60, 'days'))) {
                  return true;
                }
              }
            }
            return false;
          });
          this.sortBy = 'licenseEndDate';
          this.sortDescending = true;
          break;

        case ('License Due'):
          this.displayVehicles = this.$store.getters['vehicles/activeVehicles'].filter((item: Vehicle) => dayjs().isAfter(dayjs(item.licenseEndDate).subtract(30, 'days')));
          this.sortBy = 'licenseEndDate';
          this.sortDescending = true;
          break;

        case ('Disposed'):
          await this.listDisposed(false);
          this.displayVehicles = this.$store.getters['vehicles/disposedVehicles'];
          this.sortBy = 'disposalDate';
          this.sortDescending = false;
          break;
      }
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},        
        { type: 'Operator', level: 3},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
        { type: 'Workshop User', level: 1},
      ]);
    }

    restrictedEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Workshop User', level: 1},
      ]);
    }

    allowSuspendFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }

    allowDisposalFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }
  // #endregion

  // #region Functions required for the CSV Download 
    getDataForCSV() {
      return this.displayVehicles.reduce((a: any, b: any) => {
        let newItem = b;
        newItem = Object.assign({}, b, { 
          nextServiceDifference: b.nextServiceOdo - b.odometer, 
          nextWheelAlignmentDifference:  b.nextWheelAlignmentOdo - b.odometer,
          hasOpenDisplayLicenseJobCard: b.hasOpenDisplayLicenseJobCard ? 'YES' : 'NO' 
        });

        return a.concat(newItem);
      }, []);
    }

    getFieldsForCSV() {
      return {
        'Type': 'vehicleType',
        'Code': 'code',
        'Year': 'yearModel',
        'Make': 'make',
        'Model': 'model',
        'Registration': 'registrationNumber',
        'VIN': 'vin',
        'Odo': 'odometer',
        'Service Due At': 'nextServiceOdo',
        'KM till Next Service Due': 'nextServiceDifference',
        'Wheel Alignment Due At': 'nextWheelAlignmentOdo',
        'KM till Next Wheel Alignment Due': 'nextWheelAlignmentDifference',
        'Cell Number': 'cellNumber',
        'COF Date': 'roadworthyDate',
        'License Expiry': 'licenseEndDate',
        'Has Open Display License Job Card': 'hasOpenDisplayLicenseJobCard',
        'Status': 'vehicleStatus',
      };
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create/Update
    updateOdometer() {
      // #141 ONLY apply helper calculation on Add Vehicle
      if (this.clickedItem.id <= 0) {
        this.clickedItem.nextServiceOdo = this.clickedItem.odometer + this.clickedItem.serviceIntervalKm;
        this.clickedItem.nextWheelAlignmentOdo = this.clickedItem.odometer + this.clickedItem.wheelAlignmentIntervalKm;
      }
    }

    updateServiceInterval() {
      // #141 ONLY apply helper calculation on Add Vehicle
      if (this.clickedItem.id <= 0) {
        this.clickedItem.nextServiceOdo = this.clickedItem.odometer + this.clickedItem.serviceIntervalKm;
      }
    }

    updateWheelAlignmentInterval() {
      // #141 ONLY apply helper calculation on Add Vehicle
      if (this.clickedItem.id <= 0) {
        this.clickedItem.nextWheelAlignmentOdo = this.clickedItem.odometer + this.clickedItem.wheelAlignmentIntervalKm;
      }
    }

    async launchNewItemDialog() {
      this.clickedItem = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);      

      this.valid = false;
      this.isView = false;
      this.createDisplayLicenseJobCard = (this.restrictedEditFunctionality() ? true : false);

      // filter any that are already linked
      this.frontTrailer = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      this.unlinkedTrailers = this.$store.getters['vehicles/vehiclesByType']('Trailer').filter((item: Vehicle) => !item.isLinked);

      this.title = 'Create New Vehicle';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    selectLinkedTrailer(selectedVehicle?: Vehicle) {
      if (selectedVehicle) {
        // update the remaining fields from the Linked Trailer
        this.clickedItem.odometer = selectedVehicle.odometer;
        this.clickedItem.serviceIntervalKm = selectedVehicle.serviceIntervalKm;
        this.clickedItem.nextServiceOdo = selectedVehicle.nextServiceOdo;
        this.clickedItem.wheelAlignmentIntervalKm = selectedVehicle.wheelAlignmentIntervalKm;
        this.clickedItem.nextWheelAlignmentOdo = selectedVehicle.nextWheelAlignmentOdo;
        this.clickedItem.roadworthyDate = selectedVehicle.roadworthyDate;
        this.clickedItem.licenseEndDate = selectedVehicle.licenseEndDate;

        this.frontTrailer = selectedVehicle;
      }
    }

    launchUpdateItemDialog(item: Vehicle) {
      this.clickedItem = Object.assign({}, item);
      this.frontTrailer = this.displayVehicles.find((item: Vehicle) => item.id === this.clickedItem.linkedTrailerId);

      this.isView = false;
      this.createDisplayLicenseJobCard = (this.restrictedEditFunctionality() ? true : false);
      this.title = 'Update Vehicle';      

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    launchViewItemDialog(item: Vehicle) {
      this.clickedItem = Object.assign({}, item);
      this.frontTrailer = this.displayVehicles.find((item: Vehicle) => item.id === this.clickedItem.linkedTrailerId);

      this.isView = true;
      this.title = 'View ' + (item.disposalDate ? 'Disposed ' : '') + 'Vehicle';
      this.showItemDialog = true;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await VehicleService.create({
          vehicleTypeId: this.clickedItem.vehicleTypeId,
          code: this.clickedItem.code,
          make: this.clickedItem.make,
          model: this.clickedItem.model,
          yearModel: this.clickedItem.yearModel,
          description: this.clickedItem.description,
          vin: this.clickedItem.vin,
          registrationNumber: this.clickedItem.registrationNumber,
          odometer: this.clickedItem.odometer,
          cellNumber: this.clickedItem.cellNumber,
          roadworthyDate: this.clickedItem.roadworthyDate,
          licenseEndDate: this.clickedItem.licenseEndDate,
          wheelAlignmentIntervalKm: this.clickedItem.wheelAlignmentIntervalKm,
          nextWheelAlignmentOdo: this.clickedItem.nextWheelAlignmentOdo,
          serviceIntervalKm: this.clickedItem.serviceIntervalKm,
          nextServiceOdo: this.clickedItem.nextServiceOdo,
          linkedTrailerId: this.frontTrailer!.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            if (this.createDisplayLicenseJobCard) {
              await this.doCreateDisplayLicenseJobCard();
            }

            AppService.successHandler(response.data.message);
            await this.listActive(true);
            this.vehicleFilter = 'Active';
            this.filterVehicles();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Vehicles.vue - doCreate - 1558 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;
        this.confirmActionDelay = true;

        const response = await VehicleService.update({
          id: this.clickedItem.id,
          vehicleTypeId: this.clickedItem.vehicleTypeId,
          code: this.clickedItem.code,
          make: this.clickedItem.make,
          model: this.clickedItem.model,
          yearModel: this.clickedItem.yearModel,
          description: this.clickedItem.description,
          vin: this.clickedItem.vin,
          registrationNumber: this.clickedItem.registrationNumber,
          odometer: this.clickedItem.odometer,
          cellNumber: this.clickedItem.cellNumber,
          roadworthyDate: this.clickedItem.roadworthyDate,
          licenseEndDate: this.clickedItem.licenseEndDate,
          wheelAlignmentIntervalKm: this.clickedItem.wheelAlignmentIntervalKm,
          nextWheelAlignmentOdo: this.clickedItem.nextWheelAlignmentOdo,
          serviceIntervalKm: this.clickedItem.serviceIntervalKm,
          nextServiceOdo: this.clickedItem.nextServiceOdo,
          suspended: this.clickedItem.suspended ? this.clickedItem.suspended : false,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            if (this.createDisplayLicenseJobCard) {
              await this.doCreateDisplayLicenseJobCard();
            }

            AppService.successHandler(response.data.message);
            await this.listActive(true);
            this.filterVehicles();
            this.showItemDialog = false;
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Vehicles.vue - doUpdate - 1617 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
        this.confirmActionDelay = false;
      }
    }

    async doCreateDisplayLicenseJobCard() {
      try {
        this.clicked = true;

        const response = await JobCardService.create({
          vehicle1Id: this.clickedItem.id,
          startDate: this.getToday(),
          originalEta: dayjs().add(1, 'hour').format('YYYY-MM-DD HH:mm'),
          jobTypeId: 3, // 3: Display License
          description: 'Vehicle License Expiry Date Updated',
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Vehicles.vue - doCreateDisplayLicenseJobCard - 1654 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

  // #endregion

  // #region Functions required for the Vehicle Disposal Dialog
    launchVehicleDisposalDialog(item: Vehicle) {
      this.clickedItem = Object.assign({}, item);
      this.clickedItem.disposalDate = this.getToday();

      this.title = 'Dispose of ' + item.yearModel + ' ' + item.make + ' ' + item.model + ' (' + item.registrationNumber + ')';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showVehicleDisposalDialog = true;
    }

    async doDisposal() {
      try {
        this.clickedDisposal = true;

        const response = await VehicleService.dispose({
          id: this.clickedItem.id,
          reason: this.clickedItem.disposalReason!,
          date: this.clickedItem.disposalDate!,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listActive(true);
            await this.listDisposed(true);
            this.filterVehicles();
            this.showVehicleDisposalDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Vehicles.vue - doDisposal - 1709 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clickedDisposal = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Suspend/Activate/Delete
    launchActivateItemDialog(item: Vehicle) {
      this.clickedItem = item;

      this.action = 'ACTIVATE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #71BC78">re-activate</span> Vehicle <span style="font-weight: 700">' 
        + item.code + '</span>';

      if (item.isLinked) {
        this.dialogContent += '.<br/><br/> NOTE: The linked trailer will automatically be <span style="font-weight: 700;color: #71BC78">re-activated</span> as well';
      }

      this.dialogContent += '.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    launchSuspendItemDialog(item: Vehicle) {
      this.clickedItem = item;

      this.action = 'SUSPEND';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212 ">suspend</span> Vehicle <span style="font-weight: 700">' 
        + item.code + '</span>';

        if (item.isLinked) {
        this.dialogContent += '.<br/><br/> NOTE: The linked trailer will automatically be <span style="font-weight: 700;color: #C01212 ">suspended</span> as well';
      }
        
      this.dialogContent += '.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'SUSPEND') { 
        this.clickedItem.suspended = true;
        await this.doUpdate();

      } else if (this.action === 'ACTIVATE') {
        this.clickedItem.suspended = false;
        await this.doUpdate();
        
      }
    }
  // #endregion

}
